import { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import EmptyState from '../components/EmptyState'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'
import DocumentPreview from '../components/DocumentPreview'

interface Doc {
  doc_id?: string
  document_id?: string
  conversation_id?: string
  filename?: string
  doc_type?: string
  doc_type_friendly?: string
  confidence?: string | number
  status?: string
  risks?: string[]
  sniffed_type?: string
  reject_reason?: string
  human_doc_type?: string
  human_notes?: string
  classification_correct?: boolean
  created_at?: string
  assessment?: string | { summary?: string; quality?: string; detected_fields?: Record<string, string> }
}

interface DocsResponse { documents: Doc[]; count: number; next_key?: string }

interface Stats {
  validated: number
  pending: number
  correct: number
  accuracy: number
  by_type: Record<string, { total: number; aciertos: number; accuracy: number }>
  confusion: Record<string, Record<string, number>>
  doc_types: string[]
}

const TYPE_LABELS: Record<string, string> = {
  DNI: 'DNI',
  RECIBO_LUZ: 'Recibo de luz',
  RECIBO_AGUA: 'Recibo de agua',
  COPIA_LITERAL_SUNARP: 'Copia Literal (SUNARP)',
  HR_PU_PREDIAL: 'HR / PU predial',
  INSPEKTOR_TASACION: 'Tasación (Inspektor)',
  SUSTENTO_INGRESOS: 'Sustento de ingresos',
  FOTO_PROPIEDAD: 'Foto de propiedad',
  PERFIL_EMPRESARIO: 'Perfil de empresario',
  INFOCORP: 'Infocorp',
  FLUJO_CAJA: 'Flujo de caja',
  ACTA_MATRIMONIO: 'Acta de matrimonio',
  TESTIMONIO_HIPOTECA: 'Testimonio de hipoteca',
  TESTIMONIO_ESCRITURA: 'Escritura pública',
  OTRO: 'Otro / no clasificado',
}

const RISK_LABELS: Record<string, string> = {
  JAVASCRIPT: 'Contiene JavaScript',
  OPEN_ACTION: 'Acción al abrir',
  AUTO_ACTION: 'Acción automática',
  ARCHIVO_EMBEBIDO: 'Archivo embebido',
  ENLACE_EXTERNO: 'Enlace externo',
  DATOS_TRAS_EOF: 'Datos tras fin de archivo',
  XFA_FORM: 'Formulario XFA',
  RICH_MEDIA: 'Medios embebidos',
  MIME_DECLARADO_NO_COINCIDE: 'Tipo declarado no coincide',
}

function docId(d: Doc) { return d.doc_id ?? d.document_id ?? '' }

function confidenceOf(d: Doc) {
  const c = typeof d.confidence === 'string' ? parseFloat(d.confidence) : d.confidence
  return Number.isFinite(c) ? (c as number) : null
}

function assessmentOf(doc: Doc) {
  if (!doc.assessment) return {}
  if (typeof doc.assessment !== 'string') return doc.assessment
  try { return JSON.parse(doc.assessment) as { summary?: string; quality?: string; detected_fields?: Record<string, string> } }
  catch { return {} }
}

function ConfidenceBadge({ value }: { value: number | null }) {
  if (value === null) return <span style={{ color: 'var(--c-ink-faint)' }}>—</span>
  const alto = value >= 80
  const medio = value >= 60
  const st = alto
    ? { bg: 'var(--c-brand-light)', fg: 'var(--c-brand-hover)' }
    : medio
      ? { bg: 'var(--c-warn-light)', fg: 'var(--c-warn)' }
      : { bg: 'var(--c-danger-light)', fg: 'var(--c-danger-ink)' }
  return (
    <span style={{
      fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
      background: st.bg, color: st.fg, whiteSpace: 'nowrap',
    }}>
      {value.toFixed(0)}%
    </span>
  )
}

// ── Panel de métricas ─────────────────────────────────────────────────────────

function StatsPanel({ stats }: { stats: Stats }) {
  const confusiones = useMemo(() => {
    const filas: Array<{ real: string; detectado: string; n: number }> = []
    for (const [real, mapa] of Object.entries(stats.confusion ?? {})) {
      for (const [detectado, n] of Object.entries(mapa)) filas.push({ real, detectado, n })
    }
    return filas.sort((a, b) => b.n - a.n).slice(0, 8)
  }, [stats.confusion])

  const tipos = Object.entries(stats.by_type ?? {})
    .sort((a, b) => b[1].total - a[1].total)

  return (
    <div style={{
      display: 'grid', gap: '14px',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    }}>
      <div className="card" style={{ padding: '18px' }}>
        <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em',
                      color: 'var(--c-ink-muted)' }}>ACIERTO GLOBAL</div>
        <div style={{ fontSize: '38px', fontWeight: 800, lineHeight: 1.1, marginTop: '6px' }}>
          {stats.validated ? `${stats.accuracy}%` : '—'}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--c-ink-muted)', marginTop: '4px' }}>
          {stats.correct} correctos de {stats.validated} validados
          {stats.pending > 0 && ` · ${stats.pending} sin validar`}
        </div>
        {stats.validated === 0 && (
          <div style={{ fontSize: '11.5px', color: 'var(--c-ink-faint)', marginTop: '8px', lineHeight: 1.5 }}>
            Valida algunos documentos abajo para que aparezca el porcentaje.
          </div>
        )}
      </div>

      <div className="card" style={{ padding: '18px' }}>
        <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em',
                      color: 'var(--c-ink-muted)', marginBottom: '8px' }}>ACIERTO POR TIPO</div>
        {tipos.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--c-ink-faint)' }}>Sin datos aún.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {tipos.map(([tipo, s]) => (
              <div key={tipo} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {TYPE_LABELS[tipo] ?? tipo}
                </span>
                <span style={{ color: 'var(--c-ink-faint)', fontSize: '11px' }}>{s.total}</span>
                <ConfidenceBadge value={s.accuracy} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card" style={{ padding: '18px' }}>
        <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em',
                      color: 'var(--c-ink-muted)', marginBottom: '8px' }}>
          DÓNDE SE CONFUNDE
        </div>
        {confusiones.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--c-ink-faint)' }}>
            Sin confusiones registradas.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '12px' }}>
            {confusiones.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span style={{ fontWeight: 600 }}>{TYPE_LABELS[c.real] ?? c.real}</span>
                <span style={{ color: 'var(--c-ink-faint)' }}>→</span>
                <span style={{ color: 'var(--c-danger-ink)' }}>{TYPE_LABELS[c.detectado] ?? c.detectado}</span>
                <span style={{ marginLeft: 'auto', color: 'var(--c-ink-muted)' }}>×{c.n}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Fila de validación ────────────────────────────────────────────────────────

function DocRow({ doc, tipos, onSaved, canEdit }: {
  doc: Doc; tipos: string[]; canEdit: boolean
  onSaved: (id: string, tipo: string, correcto: boolean) => void
}) {
  const [abierto, setAbierto] = useState(false)
  const [tipo, setTipo] = useState(doc.human_doc_type ?? doc.doc_type ?? '')
  const [notas, setNotas] = useState(doc.human_notes ?? '')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const id = docId(doc)
  const detectado = doc.doc_type ?? 'OTRO'
  const validado = Boolean(doc.human_doc_type)
  const assessment = assessmentOf(doc)

  const guardar = async (tipoElegido: string) => {
    if (!id || !tipoElegido) return
    setGuardando(true); setError(null)
    try {
      const r = await apiClient.post<{ correct: boolean }>(
        `/documents/${id}/classify`, { doc_type: tipoElegido, notes: notas })
      onSaved(id, tipoElegido, r.data.correct)
      setAbierto(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    } finally { setGuardando(false) }
  }

  return (
    <div style={{ borderTop: '1px solid var(--c-surface-faint)', padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <button onClick={() => setAbierto(v => !v)} title="Ver vista previa y detalle" style={{ all: 'unset', cursor: 'pointer', flex: 1, minWidth: '180px', fontSize: '12.5px', fontWeight: 600,
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {doc.filename || id.slice(0, 12)}
        </button>

        <span style={{ fontSize: '12px', color: 'var(--c-ink-muted)', minWidth: '150px' }}>
          {TYPE_LABELS[detectado] ?? detectado}
        </span>

        <ConfidenceBadge value={confidenceOf(doc)} />

        {(doc.risks ?? []).length > 0 && (
          <span title={(doc.risks ?? []).map(r => RISK_LABELS[r] ?? r).join(', ')}
                style={{ fontSize: '11px', fontWeight: 600, color: 'var(--c-warn)',
                         background: 'var(--c-warn-light)', padding: '2px 8px', borderRadius: '20px' }}>
            ⚠ {(doc.risks ?? []).length}
          </span>
        )}

        {doc.status === 'REJECTED' && (
          <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--c-danger-ink)',
                         background: 'var(--c-danger-light)', padding: '2px 8px', borderRadius: '20px' }}>
            RECHAZADO
          </span>
        )}

        {validado && (
          <span style={{
            fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
            background: doc.classification_correct ? 'var(--c-brand-light)' : 'var(--c-danger-light)',
            color: doc.classification_correct ? 'var(--c-brand-hover)' : 'var(--c-danger-ink)',
          }}>
            {doc.classification_correct ? '✓ correcto' : `✗ era ${TYPE_LABELS[doc.human_doc_type!] ?? doc.human_doc_type}`}
          </span>
        )}

        {canEdit && (
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => void guardar(detectado)}
              disabled={guardando}
              style={{
                all: 'unset', cursor: 'pointer', fontSize: '11.5px', fontWeight: 700,
                color: 'var(--c-brand-hover)', background: 'var(--c-brand-light)',
                padding: '5px 11px', borderRadius: '8px',
              }}>
              ✓ Correcto
            </button>
            <button
              onClick={() => setAbierto(v => !v)}
              style={{
                all: 'unset', cursor: 'pointer', fontSize: '11.5px', fontWeight: 700,
                color: 'var(--c-danger-ink)', background: 'var(--c-danger-light)',
                padding: '5px 11px', borderRadius: '8px',
              }}>
              ✗ Corregir
            </button>
          </div>
        )}
      </div>

      {abierto && (
        <div style={{ marginTop: '10px', padding: '12px', background: 'var(--c-surface-subtle)',
                      borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) 1fr', gap: '12px' }}>
            <DocumentPreview documentId={id} filename={doc.filename} compact />
            <div style={{ fontSize: '12px', lineHeight: 1.5 }}>
              <div><strong>Detectado:</strong> {TYPE_LABELS[detectado] ?? detectado} ({confidenceOf(doc)?.toFixed(0) ?? '-'}%)</div>
              {assessment.quality && <div><strong>Calidad:</strong> {assessment.quality}</div>}
              {assessment.summary && <div style={{ marginTop: '6px' }}><strong>Lectura:</strong> {assessment.summary}</div>}
              {Object.entries(assessment.detected_fields ?? {}).slice(0, 4).map(([key, value]) => (
                <div key={key}><strong>{key}:</strong> {value}</div>
              ))}
            </div>
          </div>
          <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--c-ink-muted)' }}>
            ¿Qué documento era en realidad?
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {tipos.map(t => (
              <button key={t} onClick={() => setTipo(t)}
                style={{
                  all: 'unset', cursor: 'pointer', fontSize: '11.5px', fontWeight: 600,
                  padding: '5px 10px', borderRadius: '8px',
                  background: tipo === t ? 'var(--c-info-ink)' : 'white',
                  color: tipo === t ? 'white' : 'var(--c-ink-soft)',
                  border: '1px solid var(--c-surface-border)',
                }}>
                {TYPE_LABELS[t] ?? t}
              </button>
            ))}
          </div>
          <textarea
            value={notas} onChange={e => setNotas(e.target.value)}
            placeholder="Comentario para el equipo (opcional): qué lo hace reconocible, por qué falló…"
            rows={2} className="input" style={{ fontSize: '12px', resize: 'vertical' }}
          />
          {error && <ErrorAlert message={error} />}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => void guardar(tipo)} disabled={guardando || !tipo}
              className="btn-primary disabled:opacity-50" style={{ fontSize: '12px' }}>
              {guardando ? 'Guardando…' : 'Guardar corrección'}
            </button>
            <button onClick={() => setAbierto(false)}
              style={{ all: 'unset', cursor: 'pointer', fontSize: '12px',
                       color: 'var(--c-ink-muted)', padding: '6px' }}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Página ────────────────────────────────────────────────────────────────────

export default function DocValidation() {
  const { canSupervise } = useAuth()
  const canEdit = canSupervise

  const [docs, setDocs] = useState<Doc[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [soloPendientes, setSoloPendientes] = useState(true)

  const cargar = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [d, s] = await Promise.all([
        apiClient.get<DocsResponse>('/documents?limit=100'),
        apiClient.get<Stats>('/documents/classification-stats'),
      ])
      setDocs(d.data.documents ?? [])
      setStats(s.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar documentos')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void cargar() }, [cargar])

  const onSaved = (id: string, tipo: string, correcto: boolean) => {
    setDocs(prev => prev.map(d => docId(d) === id
      ? { ...d, human_doc_type: tipo, classification_correct: correcto }
      : d))
    void apiClient.get<Stats>('/documents/classification-stats')
      .then(r => setStats(r.data)).catch(() => { /* la métrica se refresca luego */ })
  }

  const visibles = useMemo(
    () => soloPendientes ? docs.filter(d => !d.human_doc_type) : docs,
    [docs, soloPendientes])

  const tipos = stats?.doc_types ?? Object.keys(TYPE_LABELS)

  if (loading) return <LoadingSpinner label="Cargando documentos..." />
  if (error) return <ErrorAlert message={error} onRetry={cargar} />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {stats && <StatsPanel stats={stats} />}

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', cursor: 'pointer' }}>
          <input type="checkbox" checked={soloPendientes}
                 onChange={e => setSoloPendientes(e.target.checked)} />
          Solo sin validar
        </label>
        <button onClick={() => void cargar()}
          style={{ all: 'unset', cursor: 'pointer', fontSize: '11.5px', fontWeight: 700,
                   color: 'var(--c-info-ink)', background: 'var(--c-info-light)',
                   padding: '5px 12px', borderRadius: '8px' }}>
          ↻ Actualizar
        </button>
      </div>

      {visibles.length === 0 ? (
        <EmptyState
          icon="📋"
          title={soloPendientes ? 'Todo validado' : 'Sin documentos'}
          description={soloPendientes
            ? 'No quedan documentos pendientes de validar.'
            : 'Los documentos que envíen los clientes aparecerán aquí.'}
        />
      ) : (
        <div style={{ background: 'white', border: '1px solid var(--c-surface-border)',
                      borderRadius: '16px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', padding: '11px 16px', background: 'var(--c-surface-subtle)',
                        fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.04em',
                        color: 'var(--c-ink-muted)' }}>
            <span style={{ flex: 1 }}>ARCHIVO</span>
            <span style={{ minWidth: '150px' }}>DETECTADO</span>
            <span>CONFIANZA</span>
          </div>
          {visibles.map(d => (
            <DocRow key={docId(d)} doc={d} tipos={tipos} canEdit={canEdit} onSaved={onSaved} />
          ))}
          <div style={{ borderTop: '1px solid var(--c-surface-faint)', padding: '10px 16px',
                        fontSize: '11.5px', color: 'var(--c-ink-muted)',
                        background: 'var(--c-surface-subtle)' }}>
            {visibles.length} de {docs.length} documentos
          </div>
        </div>
      )}

      {!canEdit && (
        <p style={{ fontSize: '12.5px', color: 'var(--c-ink-muted)' }}>
          Solo Operadores y Administradores pueden validar la clasificación.
        </p>
      )}
    </div>
  )
}
